import { es } from "date-fns/locale";
import { useFormik } from "formik";
import { useAtom } from "jotai";
import moment from "moment";
import { FC, useEffect } from "react";
import { Input, Textarea } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import { formsSubmitAtom } from "../../../atom/atom";
import CustomSelect from "../../../components/forms/CustomSelect";
import { DatePicker } from "../../../components/forms/DatePicker";
import RequiredInput from "../../../components/forms/InputHOC";
import useVehicles from "../../../hooks/api-calls/useVehicles";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { MaintenanceService } from "../../../services/maintenance/maintenanceService";
import { Maintenance } from "../../../type/entities/maintenance-type";
import { maintenanceTypes } from "../../../utils/SelectorMapping";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";

interface MaintenanceFormProps {
    setOpenModal: any;
    refetch: () => void;
    maintenance?: Maintenance | null;
    formRef?: any;
    vehicleId?: string;
    refetchStatistics?: () => void;
}

const schema = yup.object({
    vehicleId: yup.string().required('El vehículo es obligatorio'),
    type: yup.string().required('El tipo de mantenimiento es obligatorio'),
    cost: yup.number()
        .required('El coste es obligatorio')
        .min(0, 'El coste debe ser mayor o igual a 0'),
    date: yup.string().required('La fecha es obligatoria'),
    description: yup.string().nullable(),
    odometerKm: yup
        .string()
        .test('km-range', 'Los kilómetros deben ser un número mayor o igual a 0', (v) => {
            if (v === null || v === undefined || v === '') return true;
            const n = Number(String(v).replace(',', '.'));
            return !Number.isNaN(n) && n >= 0;
        })
})

const MaintenanceForm: FC<MaintenanceFormProps> = ({ setOpenModal, refetch, maintenance, formRef , vehicleId = null, refetchStatistics }) => {

    // STATES

    const service = new MaintenanceService();
    const isEdit = maintenance !== null;

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getVehiclesList, vehiclesLoading } = useVehicles({
        not_sold: true,
    });
    const { getCompaniesList } = useCompanies();
    const { userCan } = usePrivilege();
    const vehicleOptions = getVehiclesList();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: maintenance?.id ?? '',
            vehicleId: vehicleId ? vehicleId : maintenance?.vehicle?.id ?? '',
            type: maintenance?.type ?? '',
            cost: maintenance?.cost ?? 0,
            date: maintenance?.date ? moment(maintenance.date?.date).format('YYYY-MM-DD') : '',
            description: maintenance?.description ?? '',
            odometerKm:
                maintenance?.odometerKm != null && !Number.isNaN(Number(maintenance.odometerKm))
                    ? String(maintenance.odometerKm)
                    : '',
            company: maintenance?.company?.id ?? '',
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });
    const { values, setFieldValue } = formik;

    // FUNCTIONS

    const normalizePayload = (values: Record<string, unknown>) => {
        const raw = values.odometerKm;
        const n = Number(String(raw).replace(',', '.'));
        const odometerKm =
            raw === '' || raw === null || raw === undefined || Number.isNaN(n)
                ? null
                : Math.max(0, n);
        return { ...values, odometerKm };
    };

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO MANTENIMIENTO
     * @EN CREATE A NEW MAINTENANCE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createMaintenance(normalizePayload(values))).getResponseData();
            if (response.success) {
                refetch();
                refetchStatistics?.();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Mantenimiento creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN MANTENIMIENTO
     * @EN EDIT A MAINTENANCE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editMaintenance(normalizePayload(values))).getResponseData();
            if (response.success) {
                refetch();
                refetchStatistics?.();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Mantenimiento editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        const selectedVehicle = vehicleOptions.find(vehicle => vehicle.value === values.vehicleId);
        const selectedCompanyId = selectedVehicle?.companyId ?? '';

        if (selectedCompanyId !== values.company) {
            setFieldValue('company', selectedCompanyId, false);
        }
    }, [values.vehicleId, values.company, vehicleOptions, setFieldValue]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <CustomSelect
                            id='vehicleId'
                            label="Vehículo"
                            placeholder="Seleccionar vehículo"
                            containerClassName='col-span-12'
                            options={vehicleOptions}
                            value={vehicleOptions.find(vehicle => vehicle.value === formik.values.vehicleId) || null}
                            onChange={(value: any) => {
                                formik.setFieldValue('vehicleId', value?.value || '');
                                formik.setFieldValue('company', value?.companyId || '', false);
                            }}
                            error={formik.errors.vehicleId}
                            isClearable={true}
                            isSearchable={true}
                            isLoading={vehiclesLoading}
                            required={true}
                            isDisabled={isEdit || vehicleId !== null}
                        />

                        <CustomSelect
                            id='type'
                            label="Tipo de Mantenimiento"
                            placeholder="Seleccionar tipo"
                            containerClassName='col-span-12'
                            options={maintenanceTypes}
                            value={maintenanceTypes.find(type => type.value === formik.values.type) || null}
                            onChange={(value: any) => formik.setFieldValue('type', value?.value || '')}
                            error={formik.errors.type}
                            isClearable={true}
                            required={true}
                        />

                        <RequiredInput
                            id='cost'
                            label="Coste (€)"
                            type="number"
                            step="0.01"
                            min="0"
                            suffix="€"
                            className='col-span-12'
                            formik={formik}
                            error={formik.errors.cost}
                            required={true}
                        />

                        <div className='col-span-12'>
                            <span className="block text-sm font-medium text-gray-700">Fecha del mantenimiento</span>
                            <DatePicker
                                id='date'
                                label="Fecha de Mantenimiento"
                                className='col-span-12'
                                selected={formik.values.date ? new Date(formik.values.date) : null}
                                onChange={(date: Date | null) => {
                                    formik.setFieldValue('date', date ? moment(date).format('YYYY-MM-DD') : '');
                                }}
                                placeholderText="Seleccionar fecha"
                                dateFormat="dd/MM/yyyy"
                                locale={es}
                                error={formik.errors.date}
                                required
                            />
                        </div>

                        <div className='col-span-12'>
                            <Input
                                id="odometerKm"
                                type="number"
                                min={0}
                                step="any"
                                label="Kilómetros (km)"
                                placeholder="Km del vehículo en el momento del mantenimiento (opcional)"
                                value={formik.values.odometerKm}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.errors.odometerKm as string | undefined}
                            />
                        </div>

                        <div className='md:col-span-12'>
                            <Textarea
                                id='description'
                                label="Descripción"
                                placeholder="Descripción del mantenimiento (opcional)"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.errors.description}
                                rows={4}
                            />
                        </div>

                        {userCan('list', 'company') && (
                        <div className='col-span-12'>
                            <CustomSelect
                                id='company'
                                label="Compañia"
                                placeholder="Seleccionar compañia"
                                options={getCompaniesList()}
                                value={getCompaniesList().find(company => company.value === formik.values.company) || null}
                                onChange={(value: any) => formik.setFieldValue('company', value?.value || '')}
                                error={formik.errors.company}
                                isSearchable={true}
                                isClearable={true}
                                required={true}
                                isDisabled={true}
                            />
                        </div> 
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default MaintenanceForm;